/**
 * fa-app-service chat suggestions HTTP types (public chips + admin CRUD).
 *
 * Aligns with fa-app-service `chat-suggestions.types.ts` / `chat-suggestions-api.md`: Brightside
 * `AppResponse<T>` shape with `result` plus optional `payload`. Consumer list omits `active`;
 * admin payloads include {@link ChatSuggestionAdmin}.
 */

/** `result` block on success/error responses from this API surface. */
export interface ChatSuggestionsAppResult {
  status: number | string;
  code: string;
  message?: string;
}

/** Envelope for JSON bodies from `/v1/prototype/chat/suggestions` routes. */
export interface ChatSuggestionsApiResponse<TPayload = undefined> {
  result: ChatSuggestionsAppResult;
  payload?: TPayload;
}

/** Public chip row returned by `GET …/suggestions` (active-only list; no `active` field). */
export interface ChatSuggestion {
  key: string;
  /** Agent scope (e.g. `brightnav`, `content`); matches chat agent metadata `agent_id`. */
  agentId: string;
  cta: string;
  macro: string;
  version?: string | number;
  createdAt?: string;
  modifiedAt?: string;
}

/**
 * Loose row shape from DynamoDB / API before normalization (`suggestionKey` vs `key`, `active`
 * encodings). Used only by {@link normalizeRawAdminRow} in `chat-suggestions-envelope.util.ts`.
 */
export interface ChatSuggestionDynamoLike {
  key?: string;
  suggestionKey?: string;
  agentId?: string;
  cta?: string;
  macro?: string;
  active?: boolean | number | string;
  version?: string | number;
  createdAt?: string;
  modifiedAt?: string;
}

/** Admin row: public fields plus visibility (list with `includeInactive`, `GET :key`, writes). */
export interface ChatSuggestionAdmin extends Omit<ChatSuggestion, 'agentId'> {
  /** May be absent on legacy rows until assigned via admin update. */
  agentId?: string;
  active: boolean;
}

/**
 * `POST …/suggestions` body. Server generates `key` from CTA/title (slugify); do not send `key`.
 */
export interface CreateChatSuggestionBody {
  agentId: string;
  cta: string;
  macro: string;
  /** Defaults to true on the server when omitted. */
  active?: boolean;
  version?: string | number;
}

/** `PUT …/suggestions/:key` body. Omit `version` to clear stored version on the item. */
export interface UpdateChatSuggestionBody {
  agentId: string;
  cta: string;
  macro: string;
  active: boolean;
  version?: string | number;
}

/** `GET …/suggestions` (default) — `payload.suggestions` when `result.code === SERVICE_OK`. */
export interface ChatSuggestionsPublicListPayload {
  suggestions: ChatSuggestion[];
}

/** `GET …/suggestions?includeInactive=true` — each item includes `active`. */
export interface ChatSuggestionsAdminListPayload {
  suggestions: ChatSuggestionAdmin[];
}

/** `GET …/suggestions/:key`, `POST`, `PUT` — single admin-shaped row in `payload`. */
export interface ChatSuggestionsAdminSinglePayload {
  suggestion: ChatSuggestionAdmin;
}

/** Payload variants for `GET …/:key` and detail extraction fallbacks. */
export interface ChatSuggestionsAdminDetailPayload {
  suggestion?: ChatSuggestionAdmin | ChatSuggestionDynamoLike;
  suggestions?: ReadonlyArray<ChatSuggestionAdmin | ChatSuggestionDynamoLike>;
}

/** List payload allowing Dynamo-shaped rows before normalization. */
export interface ChatSuggestionsAdminListPayloadLoose {
  suggestions?: ReadonlyArray<ChatSuggestionAdmin | ChatSuggestionDynamoLike>;
}

/** Minimal shape for reading `result.status` from any chat-suggestions wire JSON. */
export type ChatSuggestionsSuccessCheckable = {
  result?: ChatSuggestionsAppResult | null;
};

/**
 * Parsed JSON for admin list (`includeInactive` or legacy top-level `suggestions`), before row
 * normalization.
 */
export type ChatSuggestionsAdminListHttpInput = ChatSuggestionsSuccessCheckable & {
  payload?: ChatSuggestionsAdminListPayloadLoose;
  suggestions?: ReadonlyArray<ChatSuggestionAdmin | ChatSuggestionDynamoLike>;
};

/** Parsed JSON for admin `GET :key` / detail extraction paths. */
export type ChatSuggestionsAdminDetailHttpInput = ChatSuggestionsSuccessCheckable & {
  payload?: ChatSuggestionsAdminDetailPayload;
};

/**
 * Successful `POST` / `PUT` response (`result` + optional `payload.suggestion`), same envelope as
 * other admin reads.
 */
export type ChatSuggestionsAdminMutationHttpInput = ChatSuggestionsApiResponse<ChatSuggestionsAdminSinglePayload>;
